import React, { useState, useEffect, useRef, useCallback } from "react";

/**
 * NewtonsLawsJeopardy — Unit 3 (Newton's Laws of Motion, Lessons 9–12) review game
 * for King's Academy. Self-contained: no external dependencies, all styles
 * namespaced under `.jpd`.
 *
 * Physical buzzers in the room; the teacher judges each answer with the
 * Correct / Wrong buttons. Wrong answers dock the value, lock the team out,
 * and leave the clue open for a steal. Two random Daily Doubles double the stakes.
 *
 * Drop-in: default export, no required props. Renders anywhere in the portal.
 */

const CATEGORIES = [
  {
    name: "Newton's First Law",
    clues: [
      { v: 100, q: "Newton's First Law is also known by this name, after the property it describes.", a: "What is the Law of Inertia?" },
      { v: 200, q: "According to the First Law, an object at rest stays at rest unless acted on by this.", a: "What is an (unbalanced/external) force?" },
      { v: 300, q: "A passenger lurches forward when a car stops suddenly. Which law explains it?", a: "What is Newton's First Law?" },
      { v: 400, q: "A hockey puck keeps sliding across the ice after being hit because of this property.", a: "What is inertia?" },
      { v: 500, q: "If gravity were suddenly switched off, an orbiting space vehicle would follow this kind of path.", a: "What is a straight line (at constant velocity)?" },
    ],
  },
  {
    name: "Newton's Second Law",
    clues: [
      { v: 100, q: "Newton's Second Law written as an equation.", a: "What is F = ma (or a = F/m)?" },
      { v: 200, q: "For the same force, a larger mass produces this kind of acceleration.", a: "What is a smaller acceleration?" },
      { v: 300, q: "If the net force is doubled and the mass stays the same, the acceleration does this.", a: "What is doubles?" },
      { v: 400, q: "A net force of 20 N acts on a 4 kg object. Its acceleration?", a: "What is 5 m/s\u00B2?" },
      { v: 500, q: "A 2 kg ball accelerates at 3 m/s\u00B2. The net force on it?", a: "What is 6 N?" },
    ],
  },
  {
    name: "Newton's Third Law",
    clues: [
      { v: 100, q: "Newton's Third Law: for every action force there is an equal and this.", a: "What is opposite reaction force?" },
      { v: 200, q: "Action and reaction forces are equal in this and opposite in this.", a: "What is magnitude (strength) and direction?" },
      { v: 300, q: "Why don't action and reaction forces cancel each other out?", a: "What is they act on two different objects?" },
      { v: 400, q: "A cannon fires a cannonball and kicks backward. This backward motion is called this.", a: "What is recoil?" },
      { v: 500, q: "A bug hits a bus windshield. Compared with the force on the bug, the force on the bus is this.", a: "What is the same (equal)?" },
    ],
  },
  {
    name: "Forces & Interactions",
    clues: [
      { v: 100, q: "In the broader sense, a force is really this between two objects.", a: "What is an interaction?" },
      { v: 200, q: "You push on a wall. Name the reaction force.", a: "What is the wall pushing back on you?" },
      { v: 300, q: "When you swim you push water backward; this is what pushes you forward.", a: "What is the water?" },
      { v: 400, q: "A rocket moves forward by pushing this in the opposite direction.", a: "What is exhaust gas?" },
      { v: 500, q: "True or false: a rocket needs air to push against in order to move.", a: "What is false (it works even better in space)?" },
    ],
  },
  {
    name: "Free Fall & Terminal Velocity",
    clues: [
      { v: 100, q: "Free fall happens when this is the only significant force acting on an object.", a: "What is gravity?" },
      { v: 200, q: "The acceleration of an object in free fall near Earth's surface.", a: "What is about 9.8 m/s\u00B2 (10 m/s\u00B2)?" },
      { v: 300, q: "As a falling object speeds up, this upward force keeps increasing.", a: "What is air resistance?" },
      { v: 400, q: "A skydiver reaches terminal velocity when air resistance does this to the force of gravity.", a: "What is becomes equal to (balances) it?" },
      { v: 500, q: "At terminal velocity, the net force has this value and the acceleration has this value.", a: "What is zero and zero?" },
    ],
  },
  {
    name: "Vectors & Net Force",
    clues: [
      { v: 100, q: "A quantity that has both magnitude and direction.", a: "What is a vector?" },
      { v: 200, q: "A quantity described by magnitude only, such as mass or speed.", a: "What is a scalar?" },
      { v: 300, q: "A vector is neatly represented by this simple drawing.", a: "What is an arrow?" },
      { v: 400, q: "Two people pull a rope to the right, one with 50 N and one with 30 N. The net force?", a: "What is 80 N to the right?" },
      { v: 500, q: "Two forces of 30 N and 40 N act at right angles. Their resultant (use Pythagoras).", a: "What is 50 N?" },
    ],
  },
];

const CSS = `
.jpd{
  --navy-deep:#060f2e; --navy:#0b1e50; --navy-tile:#0d2360; --navy-tile-hover:#123086;
  --gold:#f2c14e; --gold-bright:#ffd766; --cream:#f7f3e8; --cream-dim:#d9d3c2;
  --used:#0a1636; --terracotta:#cf6a4c; --terracotta-bright:#e07d5e;
  --green:#4c9a6a; --green-bright:#5fb37f;
  --heading:"Cambria","Georgia","Times New Roman",serif;
  --body:"Calibri","Segoe UI",system-ui,-apple-system,sans-serif;
  font-family:var(--body); color:var(--cream); background:var(--navy-deep);
  min-height:100%; width:100%; position:relative;
}
.jpd *{box-sizing:border-box;margin:0;padding:0;}
.jpd button{font-family:var(--body);cursor:pointer;border:none;}
.jpd button:focus-visible{outline:3px solid var(--gold-bright);outline-offset:2px;}

.jpd .setup{min-height:100vh;display:flex;flex-direction:column;align-items:center;
  justify-content:center;text-align:center;padding:24px;gap:26px;}
.jpd .logo{font-family:var(--heading);color:var(--gold);text-shadow:4px 4px 0 rgba(0,0,0,.45);line-height:.95;}
.jpd .logo .big{display:block;font-size:clamp(3rem,11vw,7rem);font-weight:700;letter-spacing:.02em;}
.jpd .logo .sub{display:block;font-size:clamp(.9rem,3vw,1.6rem);color:var(--cream);margin-top:.4em;letter-spacing:.28em;}
.jpd .rule{width:min(560px,80vw);height:4px;background:var(--gold);border-radius:2px;}
.jpd .panel{background:var(--navy);border:2px solid var(--gold);border-radius:14px;padding:26px 28px;
  width:min(560px,92vw);display:flex;flex-direction:column;gap:18px;}
.jpd .panel h2{font-family:var(--heading);color:var(--gold-bright);font-size:1.4rem;font-weight:700;}
.jpd .count{display:flex;gap:10px;justify-content:center;}
.jpd .count button{background:var(--navy-tile);color:var(--cream);border:2px solid transparent;
  font-size:1.4rem;font-weight:700;width:56px;height:56px;border-radius:10px;transition:background .12s,border-color .12s;}
.jpd .count button:hover{background:var(--navy-tile-hover);}
.jpd .count button.active{border-color:var(--gold);color:var(--gold-bright);background:var(--navy-tile-hover);}
.jpd .names{display:flex;flex-direction:column;gap:10px;}
.jpd .names input{font-size:1.1rem;padding:11px 14px;border-radius:8px;border:2px solid var(--navy-tile-hover);
  background:var(--navy-deep);color:var(--cream);font-family:var(--body);}
.jpd .names input:focus{outline:none;border-color:var(--gold);}
.jpd .start{background:var(--gold);color:var(--navy-deep);font-size:1.4rem;font-weight:700;padding:15px;
  border-radius:10px;font-family:var(--heading);transition:background .12s,transform .08s;}
.jpd .start:hover{background:var(--gold-bright);}
.jpd .start:active{transform:scale(.98);}
.jpd .hint{color:var(--cream-dim);font-size:.92rem;line-height:1.5;max-width:560px;}
.jpd .hint strong{color:var(--cream);}

.jpd .game{min-height:100vh;display:flex;flex-direction:column;}
.jpd .topbar{display:flex;align-items:center;justify-content:space-between;padding:10px 18px;
  background:var(--navy);border-bottom:2px solid var(--gold);gap:12px;}
.jpd .topbar h1{font-family:var(--heading);color:var(--gold);font-size:1.2rem;font-weight:700;}
.jpd .tools{display:flex;gap:8px;}
.jpd .tool{background:var(--navy-tile);color:var(--cream);font-size:.9rem;font-weight:600;padding:9px 14px;
  border-radius:8px;transition:background .12s;}
.jpd .tool:hover{background:var(--navy-tile-hover);}

.jpd .board{flex:1;display:grid;grid-template-columns:repeat(6,1fr);gap:8px;padding:12px;background:var(--navy-deep);}
.jpd .cat{background:var(--navy);color:var(--gold-bright);font-family:var(--heading);font-weight:700;
  display:flex;align-items:center;justify-content:center;text-align:center;padding:10px 6px;border-radius:8px;
  font-size:clamp(.78rem,1.3vw,1.15rem);line-height:1.15;text-transform:uppercase;letter-spacing:.01em;min-height:62px;}
.jpd .tile{background:var(--navy-tile);color:var(--gold);font-family:var(--heading);font-weight:700;
  font-size:clamp(1.4rem,3.2vw,2.5rem);display:flex;align-items:center;justify-content:center;border-radius:8px;
  transition:background .12s,transform .08s;text-shadow:2px 2px 0 rgba(0,0,0,.4);min-height:56px;}
.jpd .tile:hover{background:var(--navy-tile-hover);}
.jpd .tile:active{transform:scale(.97);}
.jpd .tile.used{background:var(--used);color:transparent;text-shadow:none;cursor:default;pointer-events:none;}

.jpd .scorebar{display:flex;gap:10px;padding:12px 14px;background:var(--navy);border-top:2px solid var(--gold);
  flex-wrap:wrap;justify-content:center;}
.jpd .team{background:var(--navy-tile);border-radius:12px;padding:10px 14px;min-width:150px;text-align:center;}
.jpd .team .name{font-weight:700;color:var(--cream);font-size:1rem;margin-bottom:2px;white-space:nowrap;
  overflow:hidden;text-overflow:ellipsis;max-width:180px;}
.jpd .team .score{font-family:var(--heading);color:var(--gold-bright);font-size:2rem;font-weight:700;line-height:1;}
.jpd .team .adj{display:flex;gap:6px;justify-content:center;margin-top:8px;}
.jpd .team .adj button{background:var(--navy-deep);color:var(--cream);font-size:1rem;font-weight:700;width:34px;
  height:30px;border-radius:6px;transition:background .12s;}
.jpd .team .adj button:hover{background:var(--navy-tile-hover);}

.jpd .overlay{position:fixed;inset:0;background:var(--navy);display:flex;flex-direction:column;align-items:center;
  justify-content:center;padding:5vh 6vw;z-index:20;text-align:center;}
.jpd .clue-meta{color:var(--gold);font-size:1.3rem;font-weight:700;letter-spacing:.05em;margin-bottom:4vh;}
.jpd .clue-text{font-family:var(--heading);color:var(--cream);font-weight:700;
  font-size:clamp(1.8rem,5vw,4rem);line-height:1.25;max-width:20ch;animation:jpdPop .18s ease-out;}
.jpd .answer-text{font-family:var(--heading);color:var(--gold-bright);font-weight:700;
  font-size:clamp(1.5rem,4.2vw,3.2rem);line-height:1.3;margin-top:5vh;max-width:24ch;}
.jpd .clue-status{color:var(--terracotta-bright);font-size:1.05rem;font-weight:600;margin-top:3vh;
  min-height:1.4em;line-height:1.4;max-width:40ch;}
.jpd .judge-row{margin-top:3.5vh;display:flex;gap:14px;flex-wrap:wrap;justify-content:center;}
.jpd .tj{background:var(--navy-tile);border-radius:12px;padding:10px 12px 12px;display:flex;flex-direction:column;
  gap:8px;min-width:150px;border:2px solid transparent;transition:opacity .12s;}
.jpd .tj .tj-name{font-weight:700;color:var(--cream);font-size:1.05rem;white-space:nowrap;overflow:hidden;
  text-overflow:ellipsis;max-width:170px;text-align:center;}
.jpd .tj .tj-btns{display:flex;gap:8px;}
.jpd .tj .tj-btns button{flex:1;font-size:1.05rem;font-weight:700;padding:12px 10px;border-radius:8px;
  transition:background .12s,transform .08s;}
.jpd .tj .tj-btns button:active{transform:scale(.96);}
.jpd .tj .correct{background:var(--green);color:#fff;}
.jpd .tj .correct:hover{background:var(--green-bright);}
.jpd .tj .wrong{background:var(--terracotta);color:#fff;}
.jpd .tj .wrong:hover{background:var(--terracotta-bright);}
.jpd .tj.out{opacity:.4;}
.jpd .tj.out .tj-name::after{content:"  \\2014 out";color:var(--terracotta-bright);font-weight:600;}
.jpd .tj.locked .tj-btns button{pointer-events:none;opacity:.5;}
.jpd .tj.out .tj-btns button{pointer-events:none;}

.jpd .clue-actions{margin-top:4vh;display:flex;gap:12px;flex-wrap:wrap;justify-content:center;}
.jpd .clue-actions button{font-size:1.15rem;font-weight:700;padding:14px 22px;border-radius:10px;
  transition:background .12s,transform .08s;}
.jpd .clue-actions button:active{transform:scale(.97);}
.jpd .reveal{background:var(--gold);color:var(--navy-deep);font-family:var(--heading);}
.jpd .reveal:hover{background:var(--gold-bright);}
.jpd .close{background:var(--navy-tile);color:var(--cream-dim);border:2px solid var(--cream-dim);}
.jpd .close:hover{background:var(--navy-tile-hover);color:var(--cream);}

.jpd .dd{position:fixed;inset:0;background:var(--navy-deep);z-index:30;display:flex;align-items:center;
  justify-content:center;flex-direction:column;cursor:pointer;}
.jpd .dd .dd-word{font-family:var(--heading);color:var(--gold);font-weight:700;
  font-size:clamp(2.5rem,10vw,7rem);text-shadow:5px 5px 0 rgba(0,0,0,.5);letter-spacing:.03em;text-align:center;
  line-height:1;animation:jpdPop .18s ease-out;}
.jpd .dd .dd-note{color:var(--cream-dim);margin-top:3vh;font-size:1.1rem;}

@keyframes jpdPop{from{transform:scale(.85);opacity:0;}to{transform:scale(1);opacity:1;}}
@media(prefers-reduced-motion:reduce){.jpd *{transition:none!important;animation:none!important;}}
@media(max-width:640px){.jpd .board{gap:5px;padding:6px;}.jpd .team{min-width:120px;}}
`;

const keyOf = (c, r) => `${c}-${r}`;

export default function NewtonsLawsJeopardy() {
  const [phase, setPhase] = useState("setup");
  const [numTeams, setNumTeams] = useState(3);
  const [names, setNames] = useState(["Team 1", "Team 2", "Team 3", "Team 4"]);
  const [scores, setScores] = useState([0, 0, 0, 0]);
  const [dd, setDd] = useState([]); // keys of the two daily doubles
  const [used, setUsed] = useState([]); // keys used
  const [active, setActive] = useState(null); // {col,row,isDD,value}
  const [settled, setSettled] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [out, setOut] = useState([]); // team indices locked out this clue
  const [gotBy, setGotBy] = useState(null);
  const [showDD, setShowDD] = useState(false);
  const rootRef = useRef(null);

  const activeNames = names.slice(0, numTeams);
  const clueValue = active ? (active.isDD ? active.value * 2 : active.value) : 0;

  const startGame = () => {
    setScores(Array(numTeams).fill(0));
    const picks = new Set();
    while (picks.size < 2) {
      picks.add(keyOf(Math.floor(Math.random() * 6), Math.floor(Math.random() * 5)));
    }
    setDd([...picks]);
    setUsed([]);
    setPhase("game");
  };

  const openClue = (c, r) => {
    const k = keyOf(c, r);
    if (used.includes(k)) return;
    const isDD = dd.includes(k);
    setActive({ col: c, row: r, isDD, value: CATEGORIES[c].clues[r].v });
    setSettled(false);
    setRevealed(false);
    setOut([]);
    setGotBy(null);
    if (isDD) setShowDD(true);
  };

  const markCorrect = (i) => {
    if (settled || revealed || out.includes(i)) return;
    setScores((s) => s.map((v, idx) => (idx === i ? v + clueValue : v)));
    setGotBy(i);
    setSettled(true);
    setRevealed(true);
  };

  const markWrong = (i) => {
    if (settled || revealed || out.includes(i)) return;
    setScores((s) => s.map((v, idx) => (idx === i ? v - clueValue : v)));
    setOut((o) => [...o, i]);
  };

  const revealAnswer = useCallback(() => {
    if (settled) return;
    setRevealed(true);
  }, [settled]);

  const closeClue = useCallback(() => {
    if (!active) return;
    setUsed((u) => [...u, keyOf(active.col, active.row)]);
    setActive(null);
  }, [active]);

  const dismissDD = () => setShowDD(false);

  const adjust = (i, delta) =>
    setScores((s) => s.map((v, idx) => (idx === i ? v + delta : v)));

  const toggleFullscreen = () => {
    const el = rootRef.current;
    if (!document.fullscreenElement) el?.requestFullscreen?.();
    else document.exitFullscreen?.();
  };

  const resetGame = () => {
    if (window.confirm("Start a brand new game? Current scores will be cleared.")) {
      setActive(null);
      setPhase("setup");
    }
  };

  // keyboard: Esc closes / dismisses DD; Space reveals
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (showDD) { setShowDD(false); return; }
        if (active) closeClue();
      }
      if (e.key === " " && active && !showDD && !settled && !revealed) {
        e.preventDefault();
        revealAnswer();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, showDD, settled, revealed, closeClue, revealAnswer]);

  const status = () => {
    if (gotBy !== null)
      return { color: "var(--green-bright)", text: `\u2713 ${activeNames[gotBy]} got it!  +$${clueValue}` };
    if (out.length >= numTeams)
      return { color: "var(--terracotta-bright)", text: "Everyone is out. Reveal the answer to move on." };
    if (out.length > 0) {
      const ns = out.map((i) => activeNames[i]);
      return {
        color: "var(--terracotta-bright)",
        text: `\u2717 ${ns.join(", ")} ${ns.length > 1 ? "are" : "is"} out.  Still open \u2014 anyone else can steal it!`,
      };
    }
    if (revealed)
      return { color: "var(--cream-dim)", text: "Answer revealed \u2014 no points awarded." };
    return { color: "", text: "" };
  };

  return (
    <div className="jpd" ref={rootRef}>
      <style>{CSS}</style>

      {phase === "setup" && (
        <section className="setup">
          <div className="logo">
            <span className="big">JEOPARDY!</span>
            <span className="sub">NEWTON'S LAWS &middot; LESSONS 9&ndash;12</span>
          </div>
          <div className="rule" />
          <div className="panel">
            <h2>How many teams?</h2>
            <div className="count">
              {[2, 3, 4].map((n) => (
                <button
                  key={n}
                  className={numTeams === n ? "active" : ""}
                  onClick={() => setNumTeams(n)}
                >
                  {n}
                </button>
              ))}
            </div>
            <div className="names">
              {Array.from({ length: numTeams }).map((_, i) => (
                <input
                  key={i}
                  type="text"
                  value={names[i]}
                  placeholder={`Team ${i + 1} name`}
                  onChange={(e) =>
                    setNames((arr) => arr.map((v, idx) => (idx === i ? e.target.value : v)))
                  }
                />
              ))}
            </div>
            <button className="start" onClick={startGame}>
              Start the game
            </button>
          </div>
          <p className="hint">
            Click a dollar value to show a clue. Teams buzz in with their bells or clickers &mdash; you judge who
            was first. Tap <strong>&#10003; Correct</strong> to award the value, or <strong>&#10007; Wrong</strong> to
            dock it and lock that team out; the clue stays open for anyone else to steal. Reveal the answer when it's
            settled. Two hidden <strong>Daily Doubles</strong> put double points at stake &mdash; win or lose. The
            &minus;/+ buttons under each team fix scores by hand.
          </p>
        </section>
      )}

      {phase === "game" && (
        <section className="game">
          <div className="topbar">
            <h1>Newton's Laws of Motion &mdash; Lessons 9&ndash;12 Review</h1>
            <div className="tools">
              <button className="tool" onClick={toggleFullscreen}>Fullscreen</button>
              <button className="tool" onClick={resetGame}>New game</button>
            </div>
          </div>

          <div className="board">
            {CATEGORIES.map((cat, c) => (
              <div className="cat" key={`h-${c}`}>{cat.name}</div>
            ))}
            {Array.from({ length: 5 }).map((_, r) =>
              CATEGORIES.map((cat, c) => {
                const k = keyOf(c, r);
                const isUsed = used.includes(k);
                return (
                  <button
                    key={k}
                    className={`tile${isUsed ? " used" : ""}`}
                    onClick={() => openClue(c, r)}
                    disabled={isUsed}
                  >
                    ${cat.clues[r].v}
                  </button>
                );
              })
            )}
          </div>

          <div className="scorebar">
            {activeNames.map((name, i) => (
              <div className="team" key={i}>
                <div className="name">{name}</div>
                <div className="score">{scores[i]}</div>
                <div className="adj">
                  <button onClick={() => adjust(i, -100)}>&minus;</button>
                  <button onClick={() => adjust(i, 100)}>+</button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {active && !showDD && (
        <div className="overlay">
          <div className="clue-meta">
            {CATEGORIES[active.col].name} &mdash; ${active.value}
            {active.isDD ? `  (Daily Double \u2014 $${clueValue} at stake!)` : ""}
          </div>
          <div className="clue-text">{CATEGORIES[active.col].clues[active.row].q}</div>
          {revealed && (
            <div className="answer-text">{CATEGORIES[active.col].clues[active.row].a}</div>
          )}
          {(() => {
            const s = status();
            return <div className="clue-status" style={{ color: s.color }}>{s.text}</div>;
          })()}

          <div className="judge-row">
            {activeNames.map((name, i) => {
              const isOut = out.includes(i);
              const locked = settled || revealed;
              return (
                <div
                  className={`tj${isOut ? " out" : ""}${locked && !isOut ? " locked" : ""}`}
                  key={i}
                >
                  <div className="tj-name">{name}</div>
                  <div className="tj-btns">
                    <button className="correct" onClick={() => markCorrect(i)}>
                      &#10003; +${clueValue}
                    </button>
                    <button className="wrong" onClick={() => markWrong(i)}>
                      &#10007; &minus;${clueValue}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="clue-actions">
            {!settled && !revealed ? (
              <>
                <button className="reveal" onClick={revealAnswer}>Reveal answer</button>
                <button className="close" onClick={closeClue}>Skip &mdash; close</button>
              </>
            ) : (
              <button className="reveal" onClick={closeClue}>Next clue &rarr;</button>
            )}
          </div>
        </div>
      )}

      {showDD && (
        <div className="dd" onClick={dismissDD}>
          <div className="dd-word">DAILY<br />DOUBLE!</div>
          <div className="dd-note">Click anywhere to see the clue &mdash; worth double points</div>
        </div>
      )}
    </div>
  );
}